import React, { ReactNode } from 'react';
import { Popover, PopoverProps } from '@mantine/core';
import UpgradeCard from './UpgradeCard';

/**
 * Props for the UpgradePopover component
 * 
 * @interface UpgradePopoverProps
 */
interface UpgradePopoverProps extends Omit<PopoverProps, 'children'> {
  /** The element that triggers the popover (usually a button or link) */
  children: ReactNode;
  /** Title text for the upgrade prompt */
  upgradeTitle: string;
  /** Description text explaining the upgrade benefits */
  upgradeDescription: string;
  /** Callback fired when user clicks the upgrade button */
  onUpgrade: () => void;
  /** Optional maximum width override for the popover */
  maxWidth?: number | string;
}

/**
 * UpgradePopover - A standardized popover component for upgrade prompts throughout the app.
 * 
 * This component provides a consistent upgrade experience by:
 * - Displaying upgrade benefits in a non-intrusive popover
 * - Maintaining consistent styling and messaging across features
 * - Handling popover positioning and responsive behavior
 * - Integrating with billing context for upgrade flow initiation
 * 
 * Used extensively for premium feature gating in:
 * - Reporting dashboard (premium charts)
 * - Staff directory (bulk operations) 
 * - Voice settings (premium voices)
 * - Advanced reception settings
 * 
 * @example
 * <UpgradePopover 
 *   opened={showUpgrade}
 *   onClose={() => setShowUpgrade(false)}
 *   position="bottom"
 * >
 *   <Button>Premium Feature</Button>
 * </UpgradePopover>
 * 
 * @param props - Component props
 * @returns JSX.Element
 */
const UpgradePopover: React.FC<UpgradePopoverProps> = ({
  children,
  upgradeTitle,
  upgradeDescription,
  onUpgrade,
  maxWidth = 360,
  position = 'right',
  withArrow = true,
  withinPortal = true,
  shadow = 'md',
  offset = 8,
  closeOnClickOutside = true,
  trapFocus = false,
  ...props
}) => {
  return (
    <Popover
      position={position}
      withArrow={withArrow}
      withinPortal={withinPortal}
      shadow={shadow}
      offset={offset}
      closeOnClickOutside={closeOnClickOutside}
      trapFocus={trapFocus}
      clickOutsideEvents={['mousedown']}
      {...props}
    >
      <Popover.Target>{children}</Popover.Target>
      <Popover.Dropdown style={{ overflow: 'visible', minWidth: 320, maxWidth: 380, border: 'none', background: 'transparent', boxShadow: 'none' }}>
        <UpgradeCard
          upgradeTitle={upgradeTitle}
          upgradeDescription={upgradeDescription}
          onUpgrade={onUpgrade}
          maxWidth={maxWidth}
        />
      </Popover.Dropdown>
    </Popover>
  );
};

export default UpgradePopover; 